<template>
  <p v-if="!hasData" class="jr-empty">Keine Daten im ausgewählten Zeitraum.</p>
  <div v-else class="mb-chart">
    <VueApexCharts type="bar" :height="resolvedHeight" :options="chartOptions" :series="series" />
  </div>
</template>

<script setup>
import { computed } from 'vue';
import VueApexCharts from 'vue3-apexcharts';
import { METRIC_COLORS } from '../composables/designTokens';
import { formatDateShort, formatNumber, parseDate } from '../composables/formatters';

const props = defineProps({
  categories: { type: Array, default: () => [] },
  // Volle bzw. technische Namen für den Tooltip, wenn categories Anzeigenamen sind.
  fullNames: { type: Array, default: () => [] },
  // [{ name, data, axis?: 'right' }] – "right" legt die Serie auf eine zweite Y-Achse.
  series: { type: Array, default: () => [] },
  // Ohne Angabe: neutrale Kennzahl-Abstufungen (Schaltungstyp-/Kanalfarben nur explizit).
  colors: { type: Array, default: () => [] },
  horizontal: { type: Boolean, default: false },
  datetime: { type: Boolean, default: false },
  // Eine Farbe pro Kategorie (z. B. Plattformfarben) statt einer Farbe pro Serie.
  distributed: { type: Boolean, default: false },
  // Raten als Zahlen-Label am Balken statt auf einer eigenen Achse.
  dataLabel: { type: Function, default: null },
  xTitle: { type: String, default: '' },
  height: { type: Number, default: 0 }
});

const hasData = computed(() => props.series.some((item) => (item.data || []).some((value) => value != null)));

// Horizontale Balken bekommen Höhe pro Kategorie, damit Beschriftungen nicht gequetscht werden.
const resolvedHeight = computed(() => {
  if (props.height) return props.height;
  if (props.horizontal) return Math.max(180, props.categories.length * 44 * Math.max(1, props.series.length * 0.6) + 60);
  return 280;
});

function formatValue(value) {
  if (value == null) return '';
  const numeric = Number(value);
  return Number.isFinite(numeric) ? formatNumber(numeric) : String(value);
}

// Apex liest xaxis.title.* – der Key darf nicht als undefined gesetzt werden.
const axisTitle = computed(() => (props.xTitle
  ? { title: { text: props.xTitle, style: { fontSize: '11px', fontWeight: 600, color: '#5d6973' } } }
  : {}));

const singleDay = computed(() => props.datetime && props.categories.length === 1);

const xaxis = computed(() => {
  if (!props.datetime) {
    return {
      categories: props.categories,
      ...axisTitle.value,
      labels: {
        trim: false,
        hideOverlappingLabels: true,
        rotate: props.horizontal ? 0 : -35,
        formatter: props.horizontal ? formatValue : undefined
      }
    };
  }
  // Ein einzelner Tag als Kategorie: Die Zeitachse setzt sonst keinen Tick genau auf den Tag,
  // und das Datum unter dem Balken fehlt. Die Balkenbreite begrenzt plotOptions (singleDay).
  if (singleDay.value) {
    // type explizit setzen: Apex mischt Updates in die alte Konfiguration und behielte sonst
    // 'datetime' – dann wird das Label "18.5." als Datum geparst und wirft einen Fehler.
    return { type: 'category', categories: [formatDateShort(props.categories[0])], ...axisTitle.value, labels: { rotate: 0 } };
  }
  return {
    type: 'datetime',
    categories: props.categories.map((value) => parseDate(value)?.getTime() || value),
    ...axisTitle.value,
    labels: { datetimeUTC: false, formatter: (value) => formatDateShort(value) }
  };
});

const yaxis = computed(() => {
  // Kategorienamen (z. B. Mailings) nicht auf 160 px abschneiden.
  if (props.horizontal) return { labels: { maxWidth: 280, formatter: (value) => (value == null ? '' : String(value)) } };
  if (props.series.some((item) => item.axis === 'right')) {
    return props.series.map((item) => ({ opposite: item.axis === 'right', min: 0, forceNiceScale: true, labels: { formatter: formatValue } }));
  }
  return { min: 0, forceNiceScale: true, labels: { formatter: formatValue } };
});

const chartOptions = computed(() => ({
  chart: {
    toolbar: { show: false },
    animations: { enabled: false },
    fontFamily: 'inherit',
    zoom: { enabled: false }
  },
  colors: props.colors.length ? props.colors : METRIC_COLORS,
  plotOptions: {
    bar: {
      horizontal: props.horizontal,
      distributed: props.distributed,
      borderRadius: 4,
      // Ein einzelner Tag würde sonst über die halbe Breite gestreckt.
      columnWidth: singleDay.value ? '12%' : '45%',
      barHeight: '70%',
      dataLabels: { position: props.horizontal ? 'center' : 'top' }
    }
  },
  dataLabels: props.dataLabel
    ? {
      enabled: true,
      formatter: (value, { dataPointIndex }) => props.dataLabel(value, dataPointIndex),
      // Horizontal steht das Label im Balken (weiß), vertikal darüber (dunkel).
      style: { fontSize: '11px', fontWeight: 700, colors: [props.horizontal ? '#fff' : '#292A2E'] },
      offsetY: props.horizontal ? 0 : -18
    }
    : { enabled: false },
  xaxis: xaxis.value,
  yaxis: yaxis.value,
  legend: { position: 'top', show: !props.distributed },
  // Mobil: Kategorienamen horizontaler Balken schmaler, sonst bleibt für die Balken kein Platz.
  responsive: props.horizontal
    ? [{ breakpoint: 720, options: { yaxis: { labels: { maxWidth: 120, style: { fontSize: '11px' } } } } }]
    : [],
  grid: { borderColor: '#e9eff1' },
  tooltip: {
    ...(props.fullNames.length
      ? { x: { formatter: (value, { dataPointIndex }) => props.fullNames[dataPointIndex] || value } }
      : {}),
    y: { formatter: formatValue }
  }
}));
</script>
